import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Coins, Zap, ShoppingBag, Copy, ExternalLink, Link2, Check, Sparkles, TrendingUp, Repeat, Users } from "lucide-react";

const PACK_STYLE = {
  credits_starter: { icon: Coins, accent: "#00f0ff", tag: "" },
  credits_pro: { icon: Zap, accent: "#a78bfa", tag: "Most popular" },
  credits_scale: { icon: TrendingUp, accent: "#36f08a", tag: "Best value" },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }); } catch { return ""; } };

export default function BillingStore({ onClose, onRefreshUser }) {
  const [tab, setTab] = useState("wallet");
  const [billing, setBilling] = useState(null);
  const [services, setServices] = useState([]);
  const [sales, setSales] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(null);
  const [linking, setLinking] = useState(null);
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([api.billingSummary(), api.storeServices()]);
      setBilling(b); setServices(s.services || []);
    } catch { toast.error("Could not load billing"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); onRefreshUser?.(); }, [load, onRefreshUser]);

  useEffect(() => {
    if (tab === "store" && sales === null) {
      api.clientSales().then(setSales).catch(() => setSales({ sales: [], total_revenue: 0, count: 0 }));
    }
  }, [tab, sales]);

  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try { const r = await api.checkout(lookup_key); window.location.href = r.checkout_url; }
    catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBuying(null); }
  };

  const getLink = async (key) => {
    setLinking(key);
    try {
      const r = await api.createServiceLink(key);
      setServices((prev) => prev.map((s) => (s.key === key ? { ...s, url: r.url } : s)));
      toast.success("Payment link ready — share it with your client");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create link"); }
    finally { setLinking(null); }
  };

  const copy = (url, key) => {
    navigator.clipboard.writeText(url);
    setCopied(key); setTimeout(() => setCopied(null), 1600);
    toast.success("Link copied");
  };

  const credits = billing?.credits ?? 0;

  return (
    <div className="h-full flex flex-col" data-testid="billing-store">
      <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-white/10">
        <h2 className="font-display uppercase tracking-widest text-cyan-300 text-lg flex items-center gap-2"><Coins size={18} /> Billing &amp; Store</h2>
        <button data-testid="billing-close" onClick={onClose} className="w-8 h-8 grid place-items-center rounded-lg bg-white/5 hover:bg-white/10 text-zinc-400"><X size={16} /></button>
      </div>

      <div className="px-5 pt-4 flex items-center gap-2">
        {[["wallet", "Credit Wallet"], ["store", "Client Store"]].map(([k, label]) => (
          <button key={k} data-testid={`billing-tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs font-tactical font-bold uppercase tracking-wider px-4 py-2 rounded-lg border transition-colors ${tab === k ? "border-cyan-400/50 bg-cyan-500/15 text-cyan-200" : "border-white/10 text-zinc-500 hover:text-zinc-300"}`}>{label}</button>
        ))}
      </div>

      {loading ? (
        <div className="flex-1 grid place-items-center text-zinc-400 font-mono text-sm"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>
      ) : (
        <div className="flex-1 overflow-y-auto thin-scroll px-5 py-5">
          {tab === "wallet" && (
            <div className="max-w-4xl">
              {/* Balance */}
              <div className="rounded-2xl border border-cyan-400/25 bg-gradient-to-br from-cyan-500/[0.08] to-transparent p-6 mb-6 flex items-end justify-between flex-wrap gap-4" data-testid="credit-balance">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400/80 font-mono mb-1">Your credit balance</div>
                  <div className="text-5xl font-display font-black text-cyan-200">{credits}<span className="text-lg text-zinc-500 ml-2 font-mono">credits</span></div>
                </div>
                <div className="text-[12px] text-zinc-400 font-mono leading-relaxed">
                  <div className="flex items-center gap-1.5"><Zap size={12} className="text-cyan-300" /> Standard task = <span className="text-zinc-200">1 credit</span></div>
                  <div className="flex items-center gap-1.5 mt-1"><Sparkles size={12} className="text-violet-300" /> Email / CRM task = <span className="text-zinc-200">2 credits</span></div>
                </div>
              </div>

              {/* Packs */}
              <div className="text-sm font-tactical font-bold uppercase tracking-wider text-zinc-300 mb-3">Top up</div>
              <div className="grid sm:grid-cols-3 gap-4 mb-8">
                {(billing?.packs || []).map((p) => {
                  const st = PACK_STYLE[p.lookup_key] || { icon: Coins, accent: "#00f0ff", tag: "" };
                  const Icon = st.icon;
                  return (
                    <div key={p.lookup_key} data-testid={`pack-${p.lookup_key}`} className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/20 transition-colors flex flex-col">
                      {st.tag && <span className="absolute -top-2.5 left-4 text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: st.accent, color: "#04070c" }}>{st.tag}</span>}
                      <Icon size={22} style={{ color: st.accent }} />
                      <div className="mt-3 text-2xl font-display font-black text-zinc-100">{p.credits}<span className="text-xs text-zinc-500 ml-1 font-mono">credits</span></div>
                      <div className="text-[12px] text-zinc-500 font-mono mt-0.5">{p.label} pack</div>
                      <div className="mt-3 text-xl font-tactical font-bold text-zinc-200">${p.price.toFixed(0)}</div>
                      <button data-testid={`buy-${p.lookup_key}`} onClick={() => buy(p.lookup_key)} disabled={buying}
                        className="mt-4 w-full h-10 rounded-xl font-tactical font-bold uppercase text-xs flex items-center justify-center gap-1.5 transition-all disabled:opacity-50"
                        style={{ background: st.accent, color: "#04070c" }}>
                        {buying === p.lookup_key ? <Loader2 size={14} className="animate-spin" /> : <Coins size={14} />} Buy credits
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Membership */}
              <div className="rounded-2xl border border-violet-400/30 bg-gradient-to-br from-violet-500/[0.1] to-transparent p-5 mb-8 flex items-center justify-between flex-wrap gap-4" data-testid="membership-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl grid place-items-center bg-violet-500/20 border border-violet-400/40"><Repeat size={18} className="text-violet-300" /></div>
                  <div>
                    <div className="text-sm font-tactical font-bold text-zinc-100">Monthly Membership</div>
                    <div className="text-[13px] text-zinc-400">Auto-refill <span className="text-violet-200 font-semibold">{(billing?.memberships?.[0]?.credits) ?? 400} credits</span> every month — never run dry mid-workflow.</div>
                  </div>
                </div>
                {billing?.membership ? (
                  <div data-testid="membership-active" className="flex items-center gap-2 text-emerald-300 font-mono text-sm px-4 py-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30"><Check size={15} /> Active · {billing.membership.credits}/mo</div>
                ) : (
                  <button data-testid="subscribe-membership" onClick={() => buy(billing?.memberships?.[0]?.lookup_key || "credits_monthly")} disabled={buying}
                    className="h-11 px-5 rounded-xl bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center gap-2 disabled:opacity-50">
                    {buying === (billing?.memberships?.[0]?.lookup_key || "credits_monthly") ? <Loader2 size={15} className="animate-spin" /> : <Repeat size={15} />} Subscribe · ${(billing?.memberships?.[0]?.price ?? 29).toFixed(0)}/mo
                  </button>
                )}
              </div>

              {/* Ledger */}
              <div className="text-sm font-tactical font-bold uppercase tracking-wider text-zinc-300 mb-3">Recent activity</div>
              <div className="rounded-xl border border-white/10 divide-y divide-white/5" data-testid="credit-ledger">
                {(billing?.ledger || []).length === 0 ? (
                  <div className="p-4 text-center text-zinc-600 text-sm">No credit activity yet. Run a task or buy a pack.</div>
                ) : billing.ledger.map((l, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-2.5 text-sm">
                    <span className="text-zinc-300 truncate">{l.label}</span>
                    <span className={`font-mono font-semibold shrink-0 ml-3 ${l.delta > 0 ? "text-emerald-400" : "text-rose-400"}`}>{l.delta > 0 ? "+" : ""}{l.delta}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === "store" && (
            <div className="max-w-4xl">
              {/* Client revenue CRM */}
              <div className="rounded-2xl border border-emerald-400/25 bg-gradient-to-br from-emerald-500/[0.07] to-transparent p-5 mb-6" data-testid="client-revenue">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-sm font-tactical font-bold uppercase tracking-wider text-emerald-200"><Users size={16} /> Client revenue</div>
                  <div className="text-right">
                    <div className="text-2xl font-display font-black text-emerald-300">${(sales?.total_revenue ?? 0).toLocaleString()}</div>
                    <div className="text-[10px] font-mono uppercase text-zinc-500">{sales?.count ?? 0} paid · all time</div>
                  </div>
                </div>
                {sales === null ? (
                  <div className="text-zinc-500 text-sm font-mono flex items-center gap-2 py-3"><Loader2 size={14} className="animate-spin" /> Checking for payments…</div>
                ) : (sales.sales || []).length === 0 ? (
                  <div className="text-zinc-500 text-sm py-3">No client payments yet. Share a service link below — paid orders show up here automatically.</div>
                ) : (
                  <div className="rounded-xl border border-white/10 divide-y divide-white/5 overflow-hidden">
                    <div className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 bg-white/[0.02]">
                      <span>Client</span><span>Service</span><span>Date</span><span className="text-right">Amount</span>
                    </div>
                    {sales.sales.map((row) => (
                      <div key={row.session_id} data-testid={`sale-${row.session_id}`} className="grid grid-cols-[1fr_1fr_auto_auto] gap-3 px-4 py-2.5 text-sm items-center">
                        <span className="text-zinc-200 truncate">{row.customer_name || row.customer_email || "—"}</span>
                        <span className="text-zinc-400 truncate">{row.service_name}</span>
                        <span className="text-zinc-500 font-mono text-[12px] whitespace-nowrap">{fmtDate(row.paid_at)}</span>
                        <span className="text-emerald-400 font-mono font-semibold text-right whitespace-nowrap">${row.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-violet-400/20 bg-violet-500/[0.05] p-4 mb-6 flex items-start gap-3">
                <ShoppingBag size={18} className="text-violet-300 mt-0.5 shrink-0" />
                <div className="text-[13px] text-zinc-300 leading-relaxed">Sell packaged services to <span className="text-violet-200 font-semibold">your own clients</span>. Generate a Stripe checkout link for any service and share it — clients pay directly, money lands in your Stripe account.</div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-testid="service-list">
                {services.map((s) => (
                  <div key={s.key} data-testid={`service-${s.key}`} className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <div className="text-lg font-tactical font-bold text-zinc-100">{s.name}</div>
                      <div className="text-right shrink-0">
                        <div className="text-xl font-display font-black text-cyan-200">${s.price.toFixed(0)}</div>
                        {s.recurring && <div className="text-[10px] font-mono text-zinc-500 uppercase">/{s.recurring}</div>}
                      </div>
                    </div>
                    <div className="text-[13px] text-zinc-400 mt-1.5 leading-relaxed flex-1">{s.blurb}</div>
                    {s.url ? (
                      <div className="mt-4 flex items-center gap-2">
                        <div className="flex-1 truncate text-[11px] font-mono text-zinc-500 bg-[#0b0e14] border border-white/10 rounded-lg px-3 py-2">{s.url}</div>
                        <button data-testid={`copy-${s.key}`} onClick={() => copy(s.url, s.key)} className="h-9 w-9 grid place-items-center rounded-lg bg-cyan-500/15 border border-cyan-400/30 text-cyan-200 hover:bg-cyan-500/25" title="Copy link">{copied === s.key ? <Check size={15} /> : <Copy size={15} />}</button>
                        <a data-testid={`open-${s.key}`} href={s.url} target="_blank" rel="noreferrer" className="h-9 w-9 grid place-items-center rounded-lg bg-white/5 border border-white/10 text-zinc-300 hover:bg-white/10" title="Open"><ExternalLink size={15} /></a>
                      </div>
                    ) : (
                      <button data-testid={`link-${s.key}`} onClick={() => getLink(s.key)} disabled={linking}
                        className="mt-4 h-10 rounded-xl bg-violet-500 hover:bg-violet-400 text-[#04070c] font-tactical font-bold uppercase text-xs flex items-center justify-center gap-1.5 disabled:opacity-50">
                        {linking === s.key ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />} Get payment link
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
